import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Spinner,
  useTheme,
  useColorMode,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Link,
  Tag,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { cqAPI, getBid } from "../../lib/Auth";
import "../CallReport/CallReport.css";

function CampaignMessages() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [conversations, setConversations] = useState([]);
  const [purpose, setPurpose] = useState("");

  const [isLoading, setIsLoading] = useState(true); // initial campaigns load
  const [isFetchingReport, setIsFetchingReport] = useState(false); // when fetching messages

  const [selectedRange, setSelectedRange] = useState("Today");
  const [sortConfig, setSortConfig] = useState({
    key: "created",
    direction: "descending",
  });

  const [dateRange, setDateRange] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const [startDate, endDate] = dateRange;

  const theme = useTheme();
  const { colorMode } = useColorMode();

  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  const formatDateTime = (value) =>
    value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "-";

  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return "-";
    const s = Number(seconds);
    if (Number.isNaN(s)) return "-";
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    if (mins <= 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const updateUrlHash = (start, end, range, campaignId) => {
    const queryParams = new URLSearchParams({
      startDate: start ? moment(start).format("YYYY-MM-DD") : "",
      endDate: end ? moment(end).format("YYYY-MM-DD") : "",
      selectedRange: range || "",
      campaign_id: campaignId || "",
    }).toString();
    window.location.hash = queryParams;
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const smsStatusSortValue = (smsStatus) => {
    if (!smsStatus) return 0;
    if (smsStatus.blocked) return 1; // blocked
    if (smsStatus.delivered) return 3; // delivered
    return 2; // not delivered (but not blocked)
  };

  const emailStatusSortValue = (emailStatus) => {
    if (!emailStatus || !emailStatus.status) return 0;
    const s = emailStatus.status;

    // Lower value = worse outcome when sorting ascending
    if (s.bounced) return 1; // worst
    if (s.spam) return 2;
    if (s.delivered) return 3; // best
    return 0;
  };

  const sortedConversations = useMemo(() => {
    const items = [...conversations];
    if (sortConfig?.key) {
      items.sort((a, b) => {
        const getVal = (obj) => {
          if (sortConfig.key === "created") {
            return new Date(obj.created);
          }

          if (sortConfig.key === "appointment_starttime") {
            return obj.appointment_starttime
              ? new Date(obj.appointment_starttime)
              : 0;
          }

          if (sortConfig.key === "status") {
            if (obj.type === "sms") return smsStatusSortValue(obj.smsStatus);
            if (obj.type === "email") return emailStatusSortValue(obj.emailStatus);
            if (obj.type === "call") {
              return typeof obj.duration === "number" ? obj.duration : 0;
            }
            return 0;
          }

          return obj[sortConfig.key];
        };

        const va = getVal(a);
        const vb = getVal(b);

        if (va < vb) return sortConfig.direction === "ascending" ? -1 : 1;
        if (va > vb) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [conversations, sortConfig]);

  const fetchCampaigns = async () => {
    setIsLoading(true);
    try {
      const response = await cqAPI.get("/campaigns");
      setCampaigns(response?.data?.campaigns?.filter((c) => !c.archived) || []);
    } catch (err) {
      console.error("Error fetching campaigns:", err);
      setCampaigns([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCampaignReport = async (params = {}) => {
    // requires a campaign
    const campaignId = params.campaignId || selectedCampaignId;
    if (!campaignId) return;

    setIsFetchingReport(true);
    try {
      const formattedStartDate = params.startDate
        ? moment(params.startDate).format("L")
        : moment(dateRange[0]).format("L");
      const formattedEndDate = params.endDate
        ? moment(params.endDate).format("L")
        : moment(dateRange[1]).format("L");

      const qs = new URLSearchParams({
        rangeBeg: formattedStartDate,
        rangeEnd: formattedEndDate,
        campaign_id: campaignId,
      });

      const response = await cqAPI.get(`/reports/campaigns?${qs.toString()}`);
      const messages = response?.data?.messages;
      const newPurpose = response?.data?.purpose || "";

      setPurpose(newPurpose);

      if (messages?.type === "ok" && Array.isArray(messages.data)) {
        setConversations(messages.data);
      } else if (Array.isArray(messages)) {
        setConversations(messages);
      } else {
        setConversations([]);
      }
    } catch (err) {
      console.error("Error fetching campaign report:", err);
      setConversations([]);
      setPurpose("");
    } finally {
      setIsFetchingReport(false);
    }
  };

  const handleDateRangeSelection = (value) => {
    setSelectedRange(value);
    let start = null;
    let end = null;

    switch (value) {
      case "Today":
        start = moment().startOf("day");
        end = moment().endOf("day");
        break;
      case "Yesterday":
        start = moment().subtract(1, "days").startOf("day");
        end = moment().subtract(1, "days").endOf("day");
        break;
      case "Last 7 days":
        start = moment().subtract(6, "days").startOf("day");
        end = moment().endOf("day");
        break;
      case "Last 4 weeks":
        start = moment().subtract(4, "weeks").startOf("day");
        end = moment().endOf("day");
        break;
      case "Month to Date":
        start = moment().startOf("month");
        end = moment().endOf("day");
        break;
      case "Quarter to date":
        start = moment().startOf("quarter");
        end = moment().endOf("day");
        break;
      case "Year to date":
        start = moment().startOf("year");
        end = moment().endOf("day");
        break;
      default:
        start = null;
        end = null;
    }

    setDateRange([start ? start.toDate() : null, end ? end.toDate() : null]);
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <Flex direction="column" width="100%" px={4}>
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500" mr={2}>
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500">
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" pt={2}>
        <Box as="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>{date.toLocaleString("default", { month: "long", year: "numeric" })}</Box>
        <Box as="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  const smsStatusLabel = (smsStatus) => {
    if (!smsStatus) return "N/A";
    if (smsStatus.blocked) return "Blocked";
    if (smsStatus.delivered) return "Delivered";
    return "Not delivered";
  };

  const smsStatusColor = (smsStatus) => {
    if (!smsStatus) return "gray";
    if (smsStatus.blocked) return "red";
    if (smsStatus.delivered) return "green";
    return "orange";
  };

  const smsStatusTitle = (smsStatus) => {
    if (!smsStatus) return "-";
    const parts = [];
    if (typeof smsStatus.blocked === "boolean") {
      parts.push(`blocked: ${smsStatus.blocked}`);
    }
    if (typeof smsStatus.delivered === "boolean") {
      parts.push(`delivered: ${smsStatus.delivered}`);
    }
    return parts.join(", ") || "-";
  };

  const emailStatusLabel = (emailStatus) => {
    if (!emailStatus || !emailStatus.status) return "N/A";
    const s = emailStatus.status;
    if (s.bounced) return "Bounced";
    if (s.spam) return "Spam";
    if (s.delivered) return "Delivered";
    return "Unknown";
  };

  const emailStatusColor = (emailStatus) => {
    if (!emailStatus || !emailStatus.status) return "gray";
    const s = emailStatus.status;
    if (s.bounced) return "red";
    if (s.spam) return "orange";
    if (s.delivered) return "green";
    return "gray";
  };

  const emailStatusLastEvent = (emailStatus) => {
    if (!emailStatus || !Array.isArray(emailStatus.history) || !emailStatus.history.length) {
      return null;
    }
    return emailStatus.history[emailStatus.history.length - 1];
  };

  const emailStatusTitle = (emailStatus) => {
    if (!emailStatus || !emailStatus.status) return "-";
    const s = emailStatus.status;
    const flags = [];
    if (typeof s.bounced === "boolean") flags.push(`bounced: ${s.bounced}`);
    if (typeof s.delivered === "boolean") flags.push(`delivered: ${s.delivered}`);
    if (typeof s.spam === "boolean") flags.push(`spam: ${s.spam}`);

    const lastEvent = emailStatusLastEvent(emailStatus);
    const lastPart = lastEvent
      ? ` | last: ${lastEvent.type} @ ${formatDateTime(lastEvent.date)}`
      : "";

    return `${flags.join(", ")}${lastPart}`;
  };

  useEffect(() => {
    // Read URL hash for persisted filters
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const s = hashParams.get("startDate")
      ? moment(hashParams.get("startDate")).toDate()
      : moment().startOf("day").toDate();
    const e = hashParams.get("endDate")
      ? moment(hashParams.get("endDate")).toDate()
      : moment().endOf("day").toDate();
    const r = hashParams.get("selectedRange") || "Today";
    const cid = hashParams.get("campaign_id") || "";

    setDateRange([s, e]);
    setSelectedRange(r);
    setSelectedCampaignId(cid);

    fetchCampaigns().then(() => {
      // Optional: if campaign_id present, auto-fetch report
      if (cid) {
        fetchCampaignReport({ startDate: s, endDate: e, campaignId: cid });
      }
    });
  }, []);

  useEffect(() => {
    updateUrlHash(startDate, endDate, selectedRange, selectedCampaignId);
  }, [startDate, endDate, selectedRange, selectedCampaignId]);

  const canShowMessages = !!selectedCampaignId && !!startDate && !!endDate;

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh" width="100%">
        <Spinner color="blue.500" size="xl" />
      </Flex>
    );
  }

  const showAppointmentColumn = purpose === "reminder";
  // Columns: Date, optional Appointment, User Name, Type, Status
  const totalColumns = 4 + (showAppointmentColumn ? 1 : 0);
  const bid = getBid()

  return (
    <Box p={4} maxHeight="100vh" overflow="hidden" width="100%">
      <Flex justifyContent="flex-start" mb={4} wrap="wrap" gap={2}>
        <Select
          placeholder="Select range"
          value={selectedRange}
          onChange={(e) => handleDateRangeSelection(e.target.value)}
          className="date-type"
          w="180px"
        >
          <option value="Today">Today</option>
          <option value="Yesterday">Yesterday</option>
          <option value="Last 7 days">Last 7 days</option>
          <option value="Last 4 weeks">Last 4 weeks</option>
          <option value="Month to Date">Month to Date</option>
          <option value="Quarter to date">Quarter to date</option>
          <option value="Year to date">Year to date</option>
          <option value="Custom">Custom</option>
        </Select>

        <DatePicker
          className={`custom-datepicker ${
            colorMode === "light" ? "light-theme" : "dark-theme"
          }`}
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={(update) => {
            setDateRange(update);
            setSelectedRange("Custom");
          }}
          isClearable
          renderCustomHeader={renderCustomHeader}
        />

        {/* Campaign dropdown (MANDATORY) */}
        <Select
          placeholder="Select campaign"
          value={selectedCampaignId}
          onChange={(e) => setSelectedCampaignId(e.target.value)}
          w="240px"
        >
          {campaigns.map((c) => (
            <option key={c._id} value={c._id}>
              {c.name}
            </option>
          ))}
        </Select>

        <Flex ml="auto">
          <Button
            onClick={() => fetchCampaignReport({ startDate, endDate })}
            isDisabled={!canShowMessages || isFetchingReport}
          >
            {isFetchingReport ? <Spinner size="sm" mr={2} /> : null}
            Show messages
          </Button>
        </Flex>
      </Flex>

      {/* Scrollable Table Container */}
      <Box overflowX="auto" overflowY="auto" maxHeight="80vh">
        <Table
          variant="simple"
          size="sm"
          minWidth="1000px"
          sx={{
            tableLayout: "fixed",
            th: {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              py: 2,
            },
            td: {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              py: 2,
            },
          }}
        >
          <Thead>
            <Tr>
              <Th onClick={() => requestSort("created")} cursor="pointer" width="170px">
                Date
              </Th>

              {showAppointmentColumn && (
                <Th
                  onClick={() => requestSort("appointment_starttime")}
                  cursor="pointer"
                  width="190px"
                >
                  Appointment Time
                </Th>
              )}

              <Th onClick={() => requestSort("user_name")} cursor="pointer" width="220px">
                Patient
              </Th>
              <Th onClick={() => requestSort("type")} cursor="pointer" width="100px">
                Type
              </Th>
              <Th onClick={() => requestSort("status")} cursor="pointer" width="200px">
                Status
              </Th>
            </Tr>
          </Thead>
          <Tbody fontSize="sm">
            {sortedConversations.map((conv, idx) => (
              <Tr
                key={conv._id || `${conv.uid}-${conv.created}-${idx}`}
                _hover={{ bg: hoverBgColor }}
              >
                <Td title={conv.created}>{formatDateTime(conv.created)}</Td>

                {showAppointmentColumn && (
                  <Td title={conv.appointment_starttime || "-"}>
                    {formatDateTime(conv.appointment_starttime)}
                  </Td>
                )}

                <Td title={conv.user_name || "-"}>
              <Link
                href={`/inbox/${conv.uid}?bid=${bid}`}
                isExternal
                color="blue.500"
                fontWeight="medium"
              >
                {conv.user_name || "-"}</Link></Td>
                
                <Td>
                  <Tag
                    size="sm"
                    colorScheme={
                      conv.type === "sms"
                        ? "blue"
                        : conv.type === "email"
                        ? "purple"
                        : conv.type === "call"
                        ? "teal"
                        : "gray"
                    }
                  >
                    {conv.type || "-"}
                  </Tag>
                </Td>

                <Td>
                  {conv.type === "sms" && (
                    <span title={smsStatusTitle(conv.smsStatus)}>
                      {conv.smsStatus ? (
                        <Tag size="sm" colorScheme={smsStatusColor(conv.smsStatus)}>
                          {smsStatusLabel(conv.smsStatus)}
                        </Tag>
                      ) : (
                        "-"
                      )}
                    </span>
                  )}

                  {conv.type === "email" && (
                    <span title={emailStatusTitle(conv.emailStatus)}>
                      {conv.emailStatus && conv.emailStatus.status ? (
                        <Tag size="sm" colorScheme={emailStatusColor(conv.emailStatus)}>
                          {emailStatusLabel(conv.emailStatus)}
                        </Tag>
                      ) : (
                        "-"
                      )}
                    </span>
                  )}

                  {conv.type === "call" && (
                    <span
                      title={
                        conv.duration != null
                          ? `${conv.duration} seconds`
                          : "-"
                      }
                    >
                      {formatDuration(conv.duration)}
                    </span>
                  )}

                  {!["sms", "email", "call"].includes(conv.type) && "-"}
                </Td>
              </Tr>
            ))}
            {!sortedConversations.length && !isFetchingReport && (
              <Tr>
                <Td colSpan={totalColumns}>
                  <Text fontSize="sm" textAlign="center" py={4}>
                    No messages found for this campaign and date range.
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

export default CampaignMessages;
